import Image from '@tiptap/extension-image';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import { isInFlight } from './bodyImages';

// Editor-only chrome around a body image: the image at half content width,
// left-aligned, with a Remove (X) button top-right. A blob: src means the S3
// upload is still in flight, so we dim it and show a spinner. None of this is in
// saved HTML — getHTML() uses the extension's renderHTML (a plain <img> carrying
// the sizing style), not this NodeView.
function BodyImageView({ node, deleteNode, selected }: NodeViewProps) {
  const src = node.attrs.src as string;
  const uploading = isInFlight(src);
  return (
    <NodeViewWrapper
      className={
        'relative my-3 inline-block w-1/2 max-w-[50%] align-top ' +
        (selected ? 'rounded-xl outline outline-2 outline-lime-600/50' : '')
      }
      data-body-image=""
    >
      <img src={src} alt="" className="block w-full rounded-xl" draggable={false} />
      {uploading && (
        <span
          className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/40"
          aria-label="Uploading image"
        >
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-lime-700 border-t-transparent" />
        </span>
      )}
      <button
        type="button"
        aria-label="Remove image"
        onClick={() => deleteNode()}
        className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full
                   bg-black/55 text-white transition-colors hover:bg-black/75 focus:outline-none
                   focus-visible:ring-2 focus-visible:ring-white/70"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.2}
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </NodeViewWrapper>
  );
}

// Block image (its own line) sized to half the content width via an inline style,
// so the sizing travels with body_html to the out-of-repo public blog. allowBase64
// lets a pasted data: image render before the scan swaps it to S3.
export const BodyImage = Image.extend({
  addNodeView() {
    return ReactNodeViewRenderer(BodyImageView);
  },
}).configure({
  inline: false,
  allowBase64: true,
  HTMLAttributes: { style: 'max-width:50%;height:auto' },
});
